/**
 * The ad's run — upload, tweet, attach — once, for the two screens that make an ad:
 * the wizard's Create (paused) (`pages/CreateAd.tsx`, after its campaign, line item and
 * criteria) and the card's Add ad (`components/AddAd.tsx`, under a PAUSED line item).
 *
 * 🔴 **Moved here from the wizard's `create()` on 2026-10-21 (M2), byte for byte in
 * meaning**: the same three X calls in the same order, each awaited; the same stop at
 * the first step that is not done; STATUS asked only while X reports processing, at
 * most `MEDIA_STATUS_ASKS_MAX` times, waiting X's own seconds; the keys the tweet is made
 * with and the tweet the attach takes are the module's answers (`mediaKeysMade`,
 * `tweetIdToPromote`), never read off a response here. A second copy of this sequence
 * on the card would have been a second place for the attach to drift from the wizard's
 * — the wizard's run was attested in production on 2026-10-21, and the card's run IS
 * the wizard's.
 *
 * What is asked of X comes in as `api`, so a case can run the sequence against answers
 * it wrote; what the screen shows goes out through `report`, so the run owns no state.
 */
import type { Answer, CardCreate, LineItemObjective, Tweet, TweetCreate } from "./api.ts";
import {
	type Draft,
	MEDIA_STATUS_ASKS_MAX,
	type Run,
	type StepState,
	adStep,
	cardMode,
	cardStep,
	defaultTweetName,
	mediaKeysMade,
	mediaKindOf,
	mediaReadiness,
	mediaStatusStep,
	mediaStep,
	mediaUploadId,
	threwStep,
	tweetIdToPromote,
	tweetStep,
	tweetText,
} from "./create-ad.ts";

/** The three X calls the run makes, as `lib/api.ts` answers them (answered, not thrown). */
export interface AdRunApi {
	uploadMedia(form: FormData): Promise<Answer>;
	mediaStatus(id: string, account: string): Promise<Answer>;
	createTweet(payload: TweetCreate): Promise<Answer>;
	createCard(payload: CardCreate): Promise<Answer>;
	promote(payload: { account: string; lineItemId: string; tweetIds: string[] }): Promise<Answer>;
}

/** The ad as typed — the wizard's draft fields, and nothing of the campaign's. */
export type AdDraft = Pick<
	Draft,
	| "adMode"
	| "adText"
	| "adName"
	| "adFiles"
	| "adUrl"
	| "adWebsiteAs"
	| "cardTitle"
	| "cardImage"
	| "adReplies"
> & {
	tweet: Tweet | null;
};

export interface AdRunInput {
	account: string;
	lineItemId: string;
	/** The line item's objective: under WEBSITE_CLICKS a written ad may go out as a card. */
	objective: LineItemObjective;
	draft: AdDraft;
}

/** Where each step's state goes, and where the run reads the states so far. */
export interface AdRunReport {
	mediaAt(index: number, state: StepState): void;
	card(state: StepState): void;
	tweet(state: StepState): void;
	ad(state: StepState): void;
	/** The run so far, for `mediaKeysMade` and `tweetIdToPromote`. */
	held(): Run;
}

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** X's `check_after_secs`, capped so a case cannot wait a minute: the wizard's own pause. */
export const defaultPause = (seconds: number) =>
	new Promise<void>((resolve) => setTimeout(resolve, Math.min(seconds, 5) * 1000));

/** X's `checkAfterSecs` in an upload's or a STATUS's answer, or null when it gives none. */
function checkAfterOf(body: unknown): number | null {
	const p = (body as { processing?: { checkAfterSecs?: number | null } } | null)?.processing;
	return typeof p?.checkAfterSecs === "number" ? p.checkAfterSecs : null;
}

/**
 * Upload each file, make the tweet, attach it. Returns when the attach has been
 * reported, or at the first step that is not done — nothing is retried, because a retry
 * makes a second object.
 */
export async function runAd(
	api: AdRunApi,
	input: AdRunInput,
	report: AdRunReport,
	pause: (seconds: number) => Promise<void> = defaultPause,
): Promise<void> {
	const { account, lineItemId, objective, draft } = input;
	const withCard = cardMode({ objective, adMode: draft.adMode, adWebsiteAs: draft.adWebsiteAs });
	if (draft.adMode === "write") {
		for (let i = 0; i < draft.adFiles.length; i++) {
			const file = draft.adFiles[i];
			const kind = mediaKindOf(file);
			if (kind === null) return;
			report.mediaAt(i, { kind: "running" });
			const form = new FormData();
			form.set("account", account);
			form.set("kind", kind);
			form.set("file", file, file.name);
			const answered = await api.uploadMedia(form).then(
				(a) => a,
				(e: unknown) => ({ status: 0, body: { error: message(e) } }),
			);
			let media = mediaStep(answered.status, answered.body);
			const uploadId = mediaUploadId(answered.body);
			// X's own seconds, from the latest answer — the upload's first, then each STATUS's
			// (🔵 #169 (а): it read the upload's for every ask).
			let after = checkAfterOf(answered.body);
			let asks = 0;
			while (
				media.kind === "done" &&
				mediaReadiness(media.processing) === "wait" &&
				uploadId !== null &&
				asks < MEDIA_STATUS_ASKS_MAX
			) {
				report.mediaAt(i, media);
				await pause(after ?? 1);
				asks += 1;
				const status = await api.mediaStatus(uploadId, account).then(
					(a) => a,
					(e: unknown) => ({ status: 0, body: { error: message(e) } }),
				);
				media = mediaStatusStep(media, status.status, status.body);
				after = checkAfterOf(status.body) ?? after;
			}
			if (media.kind === "done" && mediaReadiness(media.processing) !== "ready") {
				media = {
					kind: "stopped",
					why: "refused",
					id: media.id,
					error:
						mediaReadiness(media.processing) === "failed"
							? "X reports failed for this upload"
							: `X still reports ${media.processing ?? "processing"} after ${asks} asks`,
					mayExist: false,
				};
			}
			report.mediaAt(i, media);
			if (media.kind !== "done") return;
		}
		const keys = mediaKeysMade(report.held());
		if (keys === null) return;
		// No words and no name leave no name to give: none is sent, rather than "" (🔵 #169 (б)).
		const name = draft.adName.trim() === "" ? defaultTweetName(draft.adText) : draft.adName;
		/*
		 * 🔴 The card, when the ad goes out as one: made from the one image just uploaded, and
		 * waited for. The tweet then carries the card and NOT the image — the image is the
		 * card's, and the reference neither allows nor forbids card_uri beside media_keys — and
		 * its text carries no link (not both): the URL is the card's destination.
		 */
		let cardUri: string | null = null;
		if (withCard) {
			report.card({ kind: "running" });
			const card = await api
				.createCard({
					account,
					name,
					mediaKey: keys[0] ?? "",
					title: draft.cardTitle,
					url: draft.adUrl.trim(),
				})
				.then(
					(a) => cardStep(a.status, a.body),
					(e: unknown) => threwStep(message(e)),
				);
			report.card(card);
			// The run's one stop at the card: `cardStep` makes no done card without its cardUri,
			// and the second half says so to the type rather than leaving a second check behind.
			if (card.kind !== "done" || card.cardUri === undefined) return;
			cardUri = card.cardUri;
		}
		report.tweet({ kind: "running" });
		const tweet = await api
			.createTweet({
				account,
				...(cardUri === null
					? {
							// 🔴 The words and the link together: under WEBSITE_CLICKS X refuses a tweet
							// without one (2026-10-21), and the link is the text's last part.
							text: tweetText(draft),
							mediaKeys: keys.length > 0 ? keys : undefined,
						}
					: { text: draft.adText.trim(), cardUri }),
				...(name !== "" && { name }),
				// Sent only when chosen: absent, X's own default applies, and the console fills none in.
				...(draft.adReplies !== "" && { conversationSettings: draft.adReplies }),
			})
			.then(
				(a) => tweetStep(a.status, a.body),
				(e: unknown) => threwStep(message(e)),
			);
		report.tweet(tweet);
		if (tweet.kind !== "done") return;
	}

	const tweetId = tweetIdToPromote(report.held(), draft.tweet);
	if (tweetId === null) return;
	report.ad({ kind: "running" });
	const ad = await api.promote({ account, lineItemId, tweetIds: [tweetId] }).then(
		(a) => adStep(a.status, a.body),
		(e: unknown) => threwStep(message(e)),
	);
	report.ad(ad);
}
